import { useState } from "react";

import * as Api from "@/api";
import SaveIndicator from "@/design/components/SaveIndicator";
import InputText from "@/design/forms/InputText";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import Valkyrie, { viTrashCan, viUndo } from "@sippy-platform/valkyrie";

import { Button, Stack, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";

import contactReducer from "./contactReducer";

export default function ContactCard({ phonebook, contactGroup, contact: contactValue, location }) {
  const {
    value: contact,
    dispatch: contactDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
    isValid,
  } = useSave({
    initialValue: contactValue,
    reducer: contactReducer,
    validator: () => ({
      name: string().required({ message: "Name is required." }),
      phone1: string()
        .required({
          condition: !contact.phone2 && !contact.phone3,
          message: "Office is required when no other numbers are set.",
        })
        .pattern("^\\+?[0-9]+$", { message: "Office is not a valid number." })
        .not(contact.phone2, { message: "Office cannot be the same as Work." })
        .not(contact.phone3, { message: "Office cannot be the same as Personal." }),
      phone2: string()
        .required({
          condition: !contact.phone1 && !contact.phone3,
          message: "Work is required when no other numbers are set.",
        })
        .pattern("^\\+?[0-9]+$", { message: "Work is not a valid number." })
        .not(contact.phone1, { message: "Work cannot be the same as Office." })
        .not(contact.phone3, { message: "Work cannot be the same as Personal." }),
      phone3: string()
        .required({
          condition: !contact.phone1 && !contact.phone2,
          message: "Personal is required when no other numbers are set.",
        })
        .pattern("^\\+?[0-9]+$", { message: "Personal is not a valid number." })
        .not(contact.phone1, { message: "Personal cannot be the same as Office." })
        .not(contact.phone2, { message: "Personal cannot be the same as Work." }),
    }),
    onSubmit: () => Api.storePhonebookContact(phonebook, contactGroup, contact),
    onSuccess: (response) => {
      if (!contact.id) {
        contactDispatch({ type: "id", value: response.data.phonebookUnit.id });
      }
    },
  });

  // Handle changes
  const formHandler = (property, value) => {
    contactDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  const [loading, setLoading] = useState(false);
  function deleteContact() {
    setLoading(true);

    if (contact.id) {
      Promise.resolve(Api.deletePhonebookContact(phonebook, contactGroup, contact))
        .then(() => contactDispatch({ type: "deleteContact" }))
        .then(() => setLoading(false));
    } else {
      contactDispatch({ type: "deleteContact" });
      setLoading(false);
    }
  }

  const [restoring, setRestoring] = useState(false);
  function restoreContact() {
    setRestoring(true);

    if (Object.keys(allErrors).length === 0) {
      Promise.resolve(Api.postPhonebookContact(phonebook, contactGroup, contact))
        .then((response) => contactDispatch({ type: "restoreContact", id: response.data.phonebookUnit.id }))
        .finally(() => setRestoring(false));
    } else {
      contactDispatch({ type: "restoreContact" });
      setRestoring(false);
    }
  }

  return (
    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
      <TableCell align="center" sx={{ lineHeight: 1.365, verticalAlign: "top" }}>
        {location}
      </TableCell>
      <TableCell sx={{ py: 1, verticalAlign: "top" }} component="th" scope="row">
        {contact.deleted ? (
          <Typography sx={{ mx: 1.75, lineHeight: 2.25, color: "text.disabled" }}>{contact.name}</Typography>
        ) : (
          <InputText
            sx={{
              "& .MuiInputBase-input": {
                py: "6.5px",
              },
              "& .MuiFormLabel-root:not(.MuiInputLabel-shrink)": {
                lineHeight: "1.2em",
              },
            }}
            required
            id="name"
            label="Name"
            onChange={formHandler}
            value={contact.name}
            error={errors}
            disabled={contact.deleted}
          />
        )}
      </TableCell>
      <TableCell sx={{ py: 1, verticalAlign: "top" }}>
        {contact.deleted ? (
          <Typography sx={{ mx: 1.75, lineHeight: 2.25, color: "text.disabled" }}>{contact.company}</Typography>
        ) : (
          <InputText
            sx={{
              "& .MuiInputBase-input": {
                py: "6.5px",
              },
              "& .MuiFormLabel-root:not(.MuiInputLabel-shrink)": {
                lineHeight: "1.2em",
              },
            }}
            id="company"
            label="Company"
            onChange={formHandler}
            value={contact.company}
            error={errors}
            disabled={contact.deleted}
          />
        )}
      </TableCell>
      <TableCell sx={{ py: 1, verticalAlign: "top" }}>
        {contact.deleted ? (
          <Typography sx={{ mx: 1.75, lineHeight: 2.25, color: "text.disabled" }}>{contact.phone1}</Typography>
        ) : (
          <InputText
            sx={{
              "& .MuiInputBase-input": {
                py: "6.5px",
              },
              "& .MuiFormLabel-root:not(.MuiInputLabel-shrink)": {
                lineHeight: "1.2em",
              },
            }}
            id="phone1"
            label="Office"
            onChange={formHandler}
            value={contact.phone1}
            error={errors}
            disabled={contact.deleted}
          />
        )}
      </TableCell>
      <TableCell sx={{ py: 1, verticalAlign: "top" }}>
        {contact.deleted ? (
          <Typography sx={{ mx: 1.75, lineHeight: 2.25, color: "text.disabled" }}>{contact.phone2}</Typography>
        ) : (
          <InputText
            sx={{
              "& .MuiInputBase-input": {
                py: "6.5px",
              },
              "& .MuiFormLabel-root:not(.MuiInputLabel-shrink)": {
                lineHeight: "1.2em",
              },
            }}
            id="phone2"
            label="Work"
            onChange={formHandler}
            value={contact.phone2}
            error={errors}
            disabled={contact.deleted}
          />
        )}
      </TableCell>
      <TableCell sx={{ py: 1, verticalAlign: "top" }}>
        {contact.deleted ? (
          <Typography sx={{ mx: 1.75, lineHeight: 2.25, color: "text.disabled" }}>{contact.phone3}</Typography>
        ) : (
          <InputText
            sx={{
              "& .MuiInputBase-input": {
                py: "6.5px",
              },
              "& .MuiFormLabel-root:not(.MuiInputLabel-shrink)": {
                lineHeight: "1.2em",
              },
            }}
            id="phone3"
            label="Personal"
            onChange={formHandler}
            value={contact.phone3}
            error={errors}
            disabled={contact.deleted}
          />
        )}
      </TableCell>
      <TableCell sx={{ py: 1, verticalAlign: "top" }} component="th" scope="row">
        {contact.deleted ? (
          <Tooltip title="Restore" placement="bottom">
            <Button
              key={location}
              sx={{ mr: 1, px: 1.25, fontSize: "1rem" }}
              color="inherit"
              loading={restoring}
              onClick={restoreContact}
            >
              <Valkyrie icon={viUndo} />
            </Button>
          </Tooltip>
        ) : (
          <Stack
            direction="row"
            sx={{
              gap: 0.5,
            }}
          >
            <Tooltip title="Delete" placement="bottom">
              <Button
                key={location}
                sx={{ px: 1.25, fontSize: "1rem" }}
                color="inherit"
                loading={loading}
                onClick={deleteContact}
                disabled={!contact?.id && isValid}
              >
                <Valkyrie icon={viTrashCan} />
              </Button>
            </Tooltip>
            <SaveIndicator formState={formState} errors={allErrors} save={save} mini />
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}

ContactCard.propTypes = {
  contact: PropTypes.object,
  phonebook: PropTypes.object,
  contactGroup: PropTypes.object,
  location: PropTypes.number,
};
